import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { resendSignUpCode } from 'aws-amplify/auth';
import { useCallback, useRef, useState } from 'react';

import { ModalWrapper } from '@/components/Modal';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { OTPInput } from '@/sections/signup/OTPInput';
import { SSO } from '@/sections/signup/SSO';
import { useAuth } from '@/state/auth';

type FeedbackState = {
  type: 'success' | 'error' | null;
  message: string;
};

type ResendState = {
  isResending: boolean;
  feedback: FeedbackState;
};

// Error message constants for maintainability and future i18n
const ERROR_MESSAGES = {
  RATE_LIMIT:
    'Too many verification code requests. Please wait a few minutes before trying again.',
  GENERIC:
    'Failed to send verification code. Please try again or contact support if the issue persists.',
  SUCCESS: 'Verification code sent successfully! Check your email inbox.',
} as const;

// Design system constants for consistent theming
const FEEDBACK_STYLES = {
  SUCCESS: 'bg-success-light text-success border border-success',
  ERROR: 'bg-error-selected text-error border border-error',
} as const;

// Type guard for AWS Cognito rate limit errors
function isCognitoLimitExceededError(error: unknown): boolean {
  return (
    error !== null &&
    typeof error === 'object' &&
    'name' in error &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (error as any).name === 'LimitExceededException'
  );
}

export const EmailConfirmation = () => {
  const { confirmOTP, isLoading, credentials } = useAuth();
  const { username, password } = credentials;
  const [open, setOpen] = useState(true);
  const [resendState, setResendState] = useState<ResendState>({
    isResending: false,
    feedback: { type: null, message: '' },
  });

  // Prevent multiple simultaneous calls with ref-based debouncing
  const isResendingRef = useRef(false);

  // Debounced resend function to prevent rapid successive calls
  const handleResend = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (isResendingRef.current) {
      return;
    }

    isResendingRef.current = true;
    setResendState({
      isResending: true,
      feedback: { type: null, message: '' },
    });

    try {
      await resendSignUpCode({
        username,
      });
      setOpen(true);
      setResendState({
        isResending: false,
        feedback: {
          type: 'success',
          message: ERROR_MESSAGES.SUCCESS,
        },
      });
    } catch (error) {
      console.error('Resend email error:', error);

      const errorMessage = isCognitoLimitExceededError(error)
        ? ERROR_MESSAGES.RATE_LIMIT
        : ERROR_MESSAGES.GENERIC;

      setResendState({
        isResending: false,
        feedback: {
          type: 'error',
          message: errorMessage,
        },
      });
    } finally {
      isResendingRef.current = false;
    }
  }, [username]);

  async function confirmOtpFn(otp: string) {
    await confirmOTP(username, password, otp);
  }

  return (
    <>
      <div className="text-default-light text-sm">
        <p>
          An email was sent to{' '}
          <span className="text-default font-bold">{username}</span>
          <Button
            className="inline p-0 pl-2"
            variant="link"
            onClick={() => setOpen(true)}
          >
            Re-enter Code
          </Button>
        </p>
        <ul className="mt-4 list-inside list-disc">
          <li>{`Can't find it? Don't forget to check your spam box.`}</li>
          <li>
            {`If it's not received within 10 minutes, feel free to  `}
            <Button
              className="inline p-0"
              variant="link"
              onClick={handleResend}
            >
              send it again.
            </Button>
          </li>
        </ul>
      </div>
      <SSO />
      <ModalWrapper
        open={open}
        onClose={() => setOpen(false)}
        className="border-none"
        size="xl"
      >
        <Button
          className="text-default-light ml-auto"
          variant="ghost"
          onClick={() => setOpen(false)}
        >
          <XMarkIcon className="size-6" />
        </Button>
        <div className="p-8 pt-0 text-center">
          <h3 className="text-2xl font-semibold">Confirm Your Email Address</h3>
          <p className="text-sm font-semibold">
            Please copy & paste the six-digit code emailed to your business
            address.
          </p>

          {/* Feedback Message */}
          {resendState.feedback.type && (
            <div
              id="resend-feedback"
              role="alert"
              aria-live="polite"
              aria-atomic="true"
              data-variant={resendState.feedback.type}
              className={`mt-4 flex items-center gap-2 rounded-md p-3 text-sm ${
                resendState.feedback.type === 'success'
                  ? FEEDBACK_STYLES.SUCCESS
                  : FEEDBACK_STYLES.ERROR
              }`}
            >
              {resendState.feedback.type === 'success' ? (
                <CheckCircleIcon
                  className="size-5 shrink-0"
                  aria-hidden="true"
                />
              ) : (
                <ExclamationCircleIcon
                  className="size-5 shrink-0"
                  aria-hidden="true"
                />
              )}
              <span>{resendState.feedback.message}</span>
            </div>
          )}

          <OTPInput onSubmit={confirmOtpFn} />
          <Button disabled={isLoading} className="mt-6 w-full">
            Submit
          </Button>

          <Button
            disabled={isLoading || resendState.isResending}
            className="mt-4 w-full"
            variant="link"
            onClick={handleResend}
            aria-describedby={
              resendState.feedback.type ? 'resend-feedback' : undefined
            }
          >
            <span className="flex items-center justify-center gap-2">
              {resendState.isResending && <Spinner size="xs" />}
              Re-send Code
            </span>
          </Button>
        </div>
      </ModalWrapper>
    </>
  );
};
